import React, { ChangeEvent } from 'react';

import ColorPicker from 'components/basic/color-picker/ColorPicker';
import groupedUnits from 'components/basic/dropdowns/dataset-unit-selector/units';
import { ColumnAggregationType } from 'utils/enums';
import {
	HighlightRange,
	IProductTemplateAttribute,
} from 'components/dashboard/product-template-table/utils/interfaces';
import { Button } from 'primereact/button';
import { Chips } from 'primereact/chips';
import { Dropdown, DropdownChangeParams } from 'primereact/dropdown';
import { Fieldset } from 'primereact/fieldset';
import {
	InputNumber,
	InputNumberChangeParams,
	InputNumberValueChangeParams,
} from 'primereact/inputnumber';
import { InputSwitch, InputSwitchChangeParams } from 'primereact/inputswitch';
import { InputText } from 'primereact/inputtext';
import { Tooltip } from 'react-tooltip';

type LongAndDoubleTypesProps = {
	attribute: IProductTemplateAttribute;
	onAttrChangeInLongAndDouble: React.Dispatch<
		React.SetStateAction<IProductTemplateAttribute>
	>;
};

const LongAndDoubleTypes: React.FC<LongAndDoubleTypesProps> = ({
	attribute,
	onAttrChangeInLongAndDouble,
}) => {
	const bgHighlightList = [...(attribute.bgHighlightRanges ?? [])];

	const onBgHighlightRangeChange = (
		e: InputNumberChangeParams | InputSwitchChangeParams,
		bgRange: HighlightRange,
		i: number,
		field: 'min' | 'max',
	) => {
		let updBgRange: HighlightRange;
		if (typeof e.value !== 'boolean') {
			updBgRange = {
				...bgRange,
				[field]: bgRange[field]
					? { ...bgRange[field], value: e.value ?? 0 }
					: { value: e.value ?? 0, inclusive: false },
			};
		} else {
			updBgRange = {
				...bgRange,
				[field]: bgRange[field]
					? { ...bgRange[field], inclusive: e.value }
					: { inclusive: e.value, value: 0 },
			};
		}
		updateAttributeChange(i, updBgRange);
	};

	const updateAttributeChange = (i: number, bgRange?: HighlightRange) => {
		bgRange
			? bgHighlightList.splice(i, 1, bgRange)
			: bgHighlightList.splice(i, 1);
		onAttrChangeInLongAndDouble(prev => ({
			...prev,
			bgHighlightRanges: bgHighlightList,
		}));
	};

	const onFieldChange = (
		e:
			| DropdownChangeParams
			| InputNumberValueChangeParams
			| ChangeEvent<HTMLInputElement>,
	) =>
		onAttrChangeInLongAndDouble(prev => ({
			...prev,
			[e.target.name]: e.target.value ?? null,
		}));

	const addBgHighlightRange = () =>
		onAttrChangeInLongAndDouble(prev => ({
			...prev,
			bgHighlightRanges: [
				...bgHighlightList,
				{
					min: { value: 0, inclusive: false },
					max: { value: 0, inclusive: false },
					color: '#000000',
					bgColor: '#ffffff',
				},
			],
		}));

	const mappedBgHighlightRanges = bgHighlightList.map((bgRange, i) => (
		<div
			className='flex flex-nowrap justify-content-between gap-4 mt-1'
			key={`bg-${i}`}>
			<div className='p-field flex-1'>
				{i === 0 && <label htmlFor='min'>Min value</label>}
				<div className='p-inputgroup'>
					<InputNumber
						id='min'
						value={bgRange.min?.value ?? 0}
						name='value'
						onChange={e => onBgHighlightRangeChange(e, bgRange, i, 'min')}
						className='p-inputtext-sm'
						style={{ blockSize: '2.8rem' }}
					/>
					<div className='p-inputgroup-addon' style={{ blockSize: '2.8rem' }}>
						<Tooltip id='inclusiveMin' />
						<InputSwitch
							data-tooltip-id='inclusiveMin'
							data-tooltip-content='Inclusive min'
							checked={bgRange.min?.inclusive ?? false}
							onChange={e => onBgHighlightRangeChange(e, bgRange, i, 'min')}
							className='p-inputtext-sm'
						/>
					</div>
				</div>
			</div>

			<div className='p-field flex-1'>
				{i === 0 && <label htmlFor='max'>Max value</label>}
				<div className='p-inputgroup'>
					<InputNumber
						id='max'
						value={bgRange.max?.value ?? 0}
						onChange={e => onBgHighlightRangeChange(e, bgRange, i, 'max')}
						className='p-inputtext-sm'
						style={{ blockSize: '2.8rem' }}
					/>
					<div className='p-inputgroup-addon' style={{ blockSize: '2.8rem' }}>
						<Tooltip id='inclusiveMax' />
						<InputSwitch
							data-tooltip-content='Inclusive max'
							data-tooltip-id='inclusiveMax'
							checked={bgRange.max?.inclusive ?? false}
							onChange={e => onBgHighlightRangeChange(e, bgRange, i, 'max')}
						/>
					</div>
				</div>
			</div>

			<div className='p-field'>
				{i === 0 && (
					<label htmlFor='color' className='block'>
						Text color
					</label>
				)}
				<ColorPicker
					id='color'
					value={bgRange.color ?? '#000000'}
					onChange={e => {
						const updBgRange = { ...bgRange, color: e.target.value };
						updateAttributeChange(i, updBgRange);
					}}
				/>
			</div>
			<div className='p-field w-8rem'>
				{i === 0 && (
					<label htmlFor='color' className='block'>
						Background color
					</label>
				)}
				<ColorPicker
					id='bgColor'
					value={bgRange.bgColor}
					onChange={e => {
						const updBgRange = { ...bgRange, bgColor: e.target.value };
						updateAttributeChange(i, updBgRange);
					}}
				/>
			</div>
			<div className='p-field align-self-end'>
				<Tooltip id={`remove-${i}-btn`} />
				<Button
					data-tooltip-id={`remove-${i}-btn`}
					data-tooltip-content='Remove validation row'
					icon='pi pi-trash'
					onClick={() => updateAttributeChange(i)}
					className='p-button-outlined p-button-danger'
				/>
			</div>
		</div>
	));

	return (
		<>
			<div className='p-field p-col-2'>
				<label>Unit</label>
				<Dropdown
					value={attribute.unit}
					onChange={onFieldChange}
					name='unit'
					className='p-inputtext-sm'
					options={groupedUnits}
					optionLabel='label'
					optionGroupLabel='label'
					optionGroupChildren='items'
					editable
				/>
			</div>
			<div className=' p-field p-col-2'>
				<label>Decimal places</label>
				<InputNumber
					placeholder='Number of decimal places to show'
					value={attribute.decimalPlaces}
					onValueChange={onFieldChange}
					name='decimalPlaces'
					className='p-inputtext-sm'
				/>
			</div>

			<div className='p-field p-col-2'>
				<label>Aggregation type</label>
				<Dropdown
					options={Object.values(ColumnAggregationType)}
					value={attribute.aggregationType ?? ColumnAggregationType.SUM}
					onChange={onFieldChange}
					name='aggregationType'
					className='p-inputtext-sm'
				/>
			</div>
			<div className='p-field p-col-6'>
				<label>Evaluation expression</label>
				<InputText
					placeholder='e.g. $some_attr1 + $some_attr3 * $some_attr_2'
					value={attribute.evaluationExpression ?? ''}
					onChange={onFieldChange}
					name='evaluationExpression'
					className='p-inputtext-sm'
				/>
			</div>
			<div className='p-field p-col-9'>
				<label>Selection values</label>
				<Chips
					value={attribute.selectionValues ?? []}
					onChange={onFieldChange}
					name='selectionValues'
					separator=','
					keyfilter='num'
				/>
			</div>
			<div className='p-field p-col-3'>
				<label>Default value</label>
				<Dropdown
					value={attribute.defaultValue}
					options={attribute.selectionValues ?? []}
					onChange={onFieldChange}
					name='defaultValue'
					className='p-inputtext-sm'
					editable
				/>
			</div>

			<Fieldset legend='Output Validation' className='p-col-12'>
				{mappedBgHighlightRanges}

				<div className='p-field flex-1'>
					<Tooltip id='add-validation' />
					<Button
						data-tooltip-id='add-validation'
						data-tooltip-content='Add new validation row'
						data-tooltip-place='right'
						icon='pi pi-plus'
						onClick={addBgHighlightRange}
						className='p-button-outlined p-button-success p-button-rounded'
					/>
				</div>
			</Fieldset>
		</>
	);
};

export default LongAndDoubleTypes;
